/**
 * Property Detail Page (tracker item 54)
 * Read-only view of a customer's property at
 * /customers/:customerId/properties/:propertyId: address and details, the
 * property's balance (its share of the invoices that bill its jobs), every
 * job at the property, its discounts and recurring services. Editing happens
 * on PropertyFormPage (the Edit button).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, IDiscountsApi, IDispatchApi, IGeocodingApi, ILeadSourcesApi, IPropertiesApi, IRecurringJobsApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { decimalToCents } from '../utils/cents';
import {
  canManageBundles,
  canManageJobs,
  canManageProperties,
  canManagePropertyLocation,
  canManageRecurring,
  canReadJobs,
  canViewRecurring,
} from '../utils/permissions';
import { formatRatePercent } from '../utils/taxRate';
import { formatSize } from '../utils/priceChart';
import { CustomerJobsSection } from '../components/CustomerJobsSection';
import { BundlesSection } from '../features/service-bundles/BundlesSection';
import { RecurringServicesSection } from '../components/RecurringServicesSection';
import { AttachmentsSection } from '../components/AttachmentsSection';
import { DiscountAttachmentPanel } from '../components/DiscountAttachmentPanel';
import { Badge, StatCard, formatMoney } from '../components/DetailWidgets';
import { primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { invoiceDate, invoiceStatus } from './invoiceUi';
import { propertyDetailPath, propertyEditPath } from './propertyUi';
import { Toast } from '../components/Toast';
import { DefaultRoutePanel } from '../components/RouteControls';
import { PropertyLocationPanel } from '../components/PropertyLocationPanel';

const sectionClass = 'bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6';
const headingClass = 'text-sm font-semibold text-gray-900 uppercase tracking-wide';

export function PropertyDetailPage() {
  const navigate = useNavigate();
  const { customerId, propertyId } = useParams<{ customerId: string; propertyId: string }>();
  const { user } = useAuth();
  const api = useMemo<IPropertiesApi & IDiscountsApi & ILeadSourcesApi & IRecurringJobsApi & IDispatchApi & IGeocodingApi>(() => createAPIClient(), []);
  const canEdit = canManageProperties(user);
  const canSeeJobs = canReadJobs(user);
  const canOpenInvoices = canManageJobs(user); // /invoices/* is office-only
  const canSeeRecurring = canViewRecurring(user);
  const canEditRecurring = canManageRecurring(user);
  const canSeeLocation = canManagePropertyLocation(user);

  const [property, setProperty] = useState<types.Property | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Each section loads on its own; a failure there leaves the rest of the page.
  const [balance, setBalance] = useState<types.PropertyBalance | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [discounts, setDiscounts] = useState<types.Discount[] | null>(null);
  const [discountsError, setDiscountsError] = useState<string | null>(null);
  const [discountCatalog, setDiscountCatalog] = useState<types.Discount[]>([]);
  const [discountCatalogError, setDiscountCatalogError] = useState<string | null>(null);
  const [leadSources, setLeadSources] = useState<types.LeadSource[] | null>(null);
  const [recurring, setRecurring] = useState<types.RecurringSchedule[] | null>(null);
  const [recurringError, setRecurringError] = useState<string | null>(null);

  useEffect(() => {
    if (!propertyId) {
      setError('No property specified');
      setIsLoading(false);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    api.getProperty(propertyId)
      .then((result) => { if (active) setProperty(result); })
      .catch((err) => { if (active) { setProperty(null); setError(getErrorMessage(err, 'Failed to load property')); } })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, [api, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    let active = true;
    setBalance(null);
    setBalanceError(null);
    api.getPropertyBalance(propertyId)
      .then((result) => { if (active) setBalance(result); })
      .catch((err) => { if (active) setBalanceError(getErrorMessage(err, 'Failed to load the property balance')); });
    return () => { active = false; };
  }, [api, propertyId]);

  useEffect(() => {
    if (!propertyId) return;
    let active = true;
    setDiscounts(null);
    setDiscountsError(null);
    api.listPropertyDiscounts(propertyId)
      .then((result) => { if (active) setDiscounts(result); })
      .catch((err) => { if (active) setDiscountsError(getErrorMessage(err, 'Failed to load discounts')); });
    return () => { active = false; };
  }, [api, propertyId]);

  // The catalog feeds the add-discount select; only needed by users who can edit.
  useEffect(() => {
    if (!canEdit) return;
    let active = true;
    api.listDiscounts()
      .then((result) => { if (active) { setDiscountCatalog(result); setDiscountCatalogError(null); } })
      .catch((err) => { if (active) { setDiscountCatalog([]); setDiscountCatalogError(getErrorMessage(err, 'Failed to load discounts')); } });
    return () => { active = false; };
  }, [api, canEdit]);

  useEffect(() => {
    let active = true;
    api.listLeadSources()
      .then((result) => { if (active) setLeadSources(result); })
      .catch(() => { if (active) setLeadSources([]); });
    return () => { active = false; };
  }, [api]);

  useEffect(() => {
    if (!propertyId || !canSeeRecurring) return;
    let active = true;
    setRecurring(null);
    setRecurringError(null);
    api.listRecurringSchedules(1, 100, { propertyId })
      .then((result) => { if (active) setRecurring(result.data); })
      .catch((err) => { if (active) setRecurringError(getErrorMessage(err, 'Failed to load recurring services')); });
    return () => { active = false; };
  }, [api, propertyId, canSeeRecurring]);

  // A property reached under another customer's URL: send it to its own
  if (property && customerId && property.customerId !== customerId) {
    return <Navigate to={propertyDetailPath(property)} replace />;
  }

  const ownerId = property?.customerId ?? customerId ?? '';
  const cityStateZip = property
    ? [[property.city, property.state].filter(Boolean).join(', '), property.zip].filter(Boolean).join(' ')
    : '';
  const size = property ? formatSize(property.sizeValue, property.sizeUnit) : null;
  const leadSource = property?.leadSourceId ? leadSources?.find((source) => source.id === property.leadSourceId) : undefined;
  const leadSourceLabel = !property?.leadSourceId
    ? 'None'
    : leadSources === null ? 'Loading...' : leadSource ? leadSource.pathLabel : 'Unknown source';

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Page actions */}
      <div className="mb-6 flex flex-wrap justify-between items-center gap-3">
        <button
          onClick={() => navigate(ownerId ? `/customers/${ownerId}` : '/customers')}
          className="inline-flex items-center gap-1 min-h-[44px] sm:min-h-0 text-gray-500 hover:text-gray-900 text-sm font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {property?.customer?.name ?? 'Customer'}
        </button>
        {canEdit && property && (
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/jobs/new?customerId=${encodeURIComponent(property.customerId)}`}
              className={`${secondaryButtonClass} inline-flex items-center min-h-[44px] sm:min-h-0`}
            >
              New Job
            </Link>
            <button onClick={() => navigate(propertyEditPath(property))} className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`}>
              Edit Property
            </button>
          </div>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {isLoading && (
        <div role="status" aria-label="Loading property" className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}

      {!isLoading && property && (
        <div className="space-y-6">
          {/* Identity banner */}
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-full bg-blue-600 text-white flex items-center justify-center">
              <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 break-words">{property.address}</h1>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {cityStateZip && <span className="text-sm text-gray-600 break-words">{cityStateZip}</span>}
                {property.billingCustomerId !== property.customerId && <Badge tone="blue">Billed to another customer</Badge>}
                <span className="text-sm text-gray-500">Added {new Date(property.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Balance */}
          <PropertyBalancePanel balance={balance} loadError={balanceError} canOpenInvoices={canOpenInvoices} />

          {/* Details */}
          <section aria-labelledby="property-details-heading" className={sectionClass}>
            <h2 id="property-details-heading" className={`${headingClass} mb-4`}>Property details</h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <Detail label="Service address">
                <span className="block">{property.address}</span>
                {cityStateZip && <span className="block">{cityStateZip}</span>}
              </Detail>
              <Detail label="Owner">
                <Link to={`/customers/${property.customerId}`} className="text-blue-700 hover:underline">{property.customer?.name ?? property.customerId}</Link>
              </Detail>
              <Detail label="Billed to">
                <Link to={`/customers/${property.billingCustomerId}`} className="text-blue-700 hover:underline">{property.billingCustomer?.name ?? property.billingCustomerId}</Link>
              </Detail>
              <Detail label="Tax rate">
                {property.taxRate ? `${property.taxRate.name} (${formatRatePercent(property.taxRate.rate)})` : '—'}
              </Detail>
              <Detail label="Size">{size || 'Not set'}</Detail>
              <Detail label="Lead source">{leadSourceLabel}</Detail>
              {property.recentJobs && <Detail label="Jobs in the last 30 days">{property.recentJobs.count}</Detail>}
              <Detail label="Last updated">{new Date(property.updatedAt).toLocaleString()}</Detail>
            </dl>
          </section>

          {/* Location (tracker item 41; office roles: other roles get no coordinates) */}
          {canSeeLocation && (
            <PropertyLocationPanel
              property={property}
              api={api}
              onChanged={(fields) => setProperty((current) => (current ? { ...current, ...fields } : current))}
              className={sectionClass}
              headingClassName={headingClass}
            />
          )}

          {/* Default dispatch route (tracker item 57; office roles) */}
          <DefaultRoutePanel
            entity="property"
            entityId={property.id}
            routeId={property.routeId ?? null}
            api={api}
            canEdit={canEdit}
            onSaved={(routeId) => setProperty((current) => (current ? { ...current, routeId } : current))}
            className={sectionClass}
            headingClassName={headingClass}
          />

          {/* Discounts */}
          <section aria-labelledby="property-discounts-heading" className={sectionClass}>
            <div className="mb-4">
              <h2 id="property-discounts-heading" className={headingClass}>
                Discounts {discounts && discounts.length > 0 && <span className="text-gray-400 font-normal">({discounts.length})</span>}
              </h2>
              <p className="mt-1 text-sm text-gray-500">Applies to all services at this property (customer discounts apply too).</p>
            </div>
            <DiscountAttachmentPanel
              idPrefix={`property-${property.id}`}
              attached={discounts}
              loadError={discountsError}
              catalog={discountCatalog}
              catalogError={discountCatalogError}
              canManage={canEdit}
              emptyMessage="No discounts attached to this property"
              onAttach={async (discountId) => setDiscounts(await api.attachPropertyDiscount(property.id, discountId))}
              onDetach={async (discountId) => setDiscounts(await api.detachPropertyDiscount(property.id, discountId))}
            />
          </section>

          {/* Service bundles at this property (tracker item 85; office roles only) */}
          {canManageBundles(user) && <BundlesSection customerId={property.customerId} propertyId={property.id} properties={[property]} canManage />}

          {/* Job history: every job at this property */}
          {canSeeJobs && <CustomerJobsSection propertyId={property.id} customerId={property.customerId} canOpenEnrollments={canManageBundles(user)} />}

          {/* Documents and photos (tracker item 47; staff only) */}
          <AttachmentsSection parentType="PROPERTY" parentId={property.id} user={user} sectionClassName={sectionClass} />

          {/* Recurring services at this property */}
          {canSeeRecurring && (
            <RecurringServicesSection
              customerId={property.customerId}
              propertyId={property.id}
              properties={[property]}
              schedules={recurring}
              loadError={recurringError}
              canManage={canEditRecurring}
            />
          )}
        </div>
      )}
    </main>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 break-words">{children}</dd>
    </div>
  );
}

interface PropertyBalancePanelProps {
  /** null while loading */
  balance: types.PropertyBalance | null;
  loadError: string | null;
  canOpenInvoices: boolean;
}

/**
 * The property's invoiced / paid / outstanding amounts and the invoices they
 * come from. Amounts are computed by the server in cents; shared invoices
 * (billing several properties) count only this property's lines, with
 * payments split pro rata.
 */
function PropertyBalancePanel({ balance, loadError, canOpenInvoices }: PropertyBalancePanelProps) {
  const hasShared = Boolean(balance?.invoices.some((invoice) => invoice.shared));
  return (
    <section aria-labelledby="property-balance-heading" className={sectionClass}>
      <h2 id="property-balance-heading" className={`${headingClass} mb-1`}>Balance</h2>
      <p className="mb-4 text-sm text-gray-500">
        From posted invoices for this property's work. Draft and voided invoices are left out.
      </p>
      {loadError ? (
        <><Toast kind="error">{loadError}</Toast><p className="text-gray-500 text-sm">The balance could not be loaded.</p></>
      ) : balance === null ? (
        <p role="status" className="text-gray-500 text-sm">Loading balance...</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <StatCard label="Total Invoiced" value={formatMoney(balance.totalInvoiced)} />
            <StatCard label="Total Paid" value={formatMoney(balance.totalPaid)} />
            <StatCard label="Balance Due" value={formatMoney(balance.balanceDue)} accent={(decimalToCents(balance.balanceDue) ?? 0) > 0} />
            <StatCard label="Invoices" value={balance.invoiceCount} />
          </div>
          {balance.invoices.length === 0 ? (
            <p className="text-gray-500 text-sm">No invoices for this property yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Invoice', 'Date', 'Status', 'This property', 'Paid', 'Outstanding'].map((heading) => (
                      <th key={heading} scope="col" className="px-4 py-3 text-left text-gray-600 font-medium">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {balance.invoices.map((invoice) => (
                    <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                      <td data-label="Invoice" data-primary className="px-4 py-3 font-semibold">
                        {canOpenInvoices ? (
                          <Link to={`/invoices/${invoice.invoiceId}`} className="text-blue-700 hover:underline">#{invoice.invoiceNumber}</Link>
                        ) : (
                          <span>#{invoice.invoiceNumber}</span>
                        )}
                        {invoice.shared && (
                          <span className="ml-2 align-middle font-normal" title={`Invoice total ${formatMoney(invoice.invoiceTotal)}, paid ${formatMoney(invoice.invoicePaid)}`}>
                            <Badge tone="gray">Shared</Badge>
                          </span>
                        )}
                      </td>
                      <td data-label="Date" className="px-4 py-3">{invoiceDate(invoice.invoiceDate)}</td>
                      <td data-label="Status" className="px-4 py-3">{invoiceStatus(invoice.status)}</td>
                      <td data-label="This property" className="px-4 py-3">{formatMoney(invoice.propertyAmount)}</td>
                      <td data-label="Paid" className="px-4 py-3">{formatMoney(invoice.propertyPaid)}</td>
                      <td data-label="Outstanding" className="px-4 py-3">{formatMoney(invoice.propertyBalance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {hasShared && (
            <p className="text-xs text-gray-500">
              Shared invoices also bill other properties: only this property's lines are counted, and payments on them
              are split in proportion to each property's share of the invoice.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
